// tab-container.js
class TabContainer extends HTMLElement {
  constructor() {
    super();
    // this.attachShadow({ mode: 'open' });

    this.tablist = null;
    this.tabs = [];
    this.panels = [];
  }

  connectedCallback() {
    this._setupPanels();
    this._createTablist();
    this._addEventListeners();
    this._render();

    if (this.shadowRoot) {
      this._loadStyles();
    }
  }

  async _loadStyles() {
    try {
      const styleURL = this.dataset.style;
      const response = await fetch(styleURL);
      const styles = await response.text();
      const styleSheet = new CSSStyleSheet();
      styleSheet.replaceSync(styles);
      this.shadowRoot.adoptedStyleSheets = [styleSheet];
    } catch (error) {
      console.error('Impossibile caricare gli stili:', error);
    }
  }

  _setupPanels() {
    // Trova tutti i pannelli e crea i tab corrispondenti
    this.panels = Array.from(this.children).filter(child =>
      child.hasAttribute('data-tab-title')
    );

    // Imposta gli attributi ARIA e l'id per ogni pannello
    this.panels.forEach((panel, index) => {
      const panelId = `panel-${this._generateId()}-${index}`;
      const tabId = `tab-${this._generateId()}-${index}`;

      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('id', panelId);
      panel.setAttribute('aria-labelledby', tabId);
      panel.setAttribute('tabindex', index === 0 ? '0' : '-1');

      if (index !== 0) {
        panel.hidden = true;
      }
    });
  }

  _createTablist() {
    this.tablist = document.createElement('div');
    this.tablist.setAttribute('role', 'tablist');
    this.tablist.classList.add('tablist');

    // Crea i tab basati sui pannelli
    this.panels.forEach((panel, index) => {
      const tabId = panel.getAttribute('aria-labelledby');
      const button = document.createElement('button');
      const title = panel.getAttribute('data-tab-title');

      button.setAttribute('role', 'tab');
      button.setAttribute('id', tabId);
      button.setAttribute('aria-controls', panel.id);
      button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      button.setAttribute('tabindex', index === 0 ? '0' : '-1');
      button.textContent = title;

      this.tabs.push(button);
      this.tablist.appendChild(button);
    });
  }

  _render() {
    const container = document.createElement('div');
    container.classList.add('tab-container');

    // Sposta i pannelli in un contenitore
    const panelsContainer = document.createElement('div');
    panelsContainer.classList.add('panels-container');
    this.panels.forEach(panel => {
      panelsContainer.appendChild(panel);
    });

    container.appendChild(this.tablist);
    container.appendChild(panelsContainer);

    if (this.shadowRoot) {
      this.shadowRoot.appendChild(container);
    } else {
      this.appendChild(container);
    }
  }

  _addEventListeners() {
    this.tabs.forEach(tab => {
      tab.addEventListener('click', () => this._selectTab(tab));

      tab.addEventListener('keydown', (e) => {
        const targetTab = this._getTargetTab(e);
        if (targetTab) {
          e.preventDefault();
          this._selectTab(targetTab);
        }
      });
    });
  }

  _selectTab(tab) {
    // Deseleziona tutti i tab
    this.tabs.forEach(t => {
      t.setAttribute('aria-selected', 'false');
      t.setAttribute('tabindex', '-1');
    });

    // Seleziona il tab corrente
    tab.setAttribute('aria-selected', 'true');
    tab.setAttribute('tabindex', '0');
    tab.focus();
    tab.scrollIntoView({inline: "center", block: "nearest"});

    // Mostra il pannello corrispondente
    const panelId = tab.getAttribute('aria-controls');
    this.panels.forEach(panel => {
      panel.hidden = panel.id !== panelId;
      panel.setAttribute('tabindex', panel.id !== panelId ? '-1' : '0');
    });
  }

  _getTargetTab(e) {
    const currentIndex = this.tabs.indexOf(e.target);

    switch(e.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        return this.tabs[currentIndex - 1] || this.tabs[this.tabs.length - 1];
      case 'ArrowRight':
      case 'ArrowDown':
        return this.tabs[currentIndex + 1] || this.tabs[0];
      case 'Home':
        return this.tabs[0];
      case 'End':
        return this.tabs[this.tabs.length - 1];
      default:
        return null;
    }
  }

  _generateId() {
    return Math.random().toString(36).substring(2, 9);
  }

  selectTab(index) {
    this.tabs[index].click();
  }
}

customElements.define('tab-container', TabContainer);
